/**
 * Date formatting utilities
 */

/**
 * Format date to user-friendly format
 * @param {string|Date} dateString - Date string or Date object
 * @param {string} format - Format type ('short', 'long', 'time', 'datetime')
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, format = "long") => {
  if (!dateString) return "Not available";

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }

    switch (format) {
      case "short":
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }); // e.g., "Jun 19, 2024"

      case "long":
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }); // e.g., "June 19, 2024"

      case "time":
        return date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }); // e.g., "11:01 AM"

      case "datetime":
        return date.toLocaleString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }); // e.g., "June 19, 2024 at 11:01 AM"

      case "relative":
        return getRelativeTime(date);

      default:
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
    }
  } catch (error) {
    console.error("Date formatting error:", error);
    return "Invalid date";
  }
};

/**
 * Get relative time (e.g., "2 days ago", "3 months ago")
 * @param {Date} date - Date object
 * @returns {string} Relative time string
 */
export const getRelativeTime = (date) => {
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return "Just now";
  if (diffInSeconds < 3600)
    return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400)
    return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000)
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 31536000)
    return `${Math.floor(diffInSeconds / 2592000)} months ago`;

  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
};

/**
 * Format date for API requests (ISO format)
 * @param {Date} date - Date object
 * @returns {string} ISO date string
 */
export const formatDateForAPI = (date) => {
  if (!date) return null;
  try {
    return new Date(date).toISOString();
  } catch (error) {
    console.error("Date formatting for API error:", error);
    return null;
  }
};

/**
 * Check if date is today
 * @param {string|Date} dateString - Date string or Date object
 * @returns {boolean} True if date is today
 */
export const isToday = (dateString) => {
  if (!dateString) return false;

  try {
    const date = new Date(dateString);
    const today = new Date();

    return date.toDateString() === today.toDateString();
  } catch (error) {
    return false;
  }
};

/**
 * Check if date is in the past
 * @param {string|Date} dateString - Date string or Date object
 * @returns {boolean} True if date is in the past
 */
export const isPast = (dateString) => {
  if (!dateString) return false;

  try {
    const date = new Date(dateString);
    const now = new Date();

    return date < now;
  } catch (error) {
    return false;
  }
};
